import { COMBO_LETTERS, SKATE_LETTERS, touches, type Collectable, type CollectableSpot } from "./collectables";
import type { Skater, SkaterEvent } from "./skater";
import { comboEntries, TASK_KINDS, type LandedCombo, type Riding, type TaskGoal } from "./tasks";

// A THPS career-style run: two minutes on a map with a goal list. Kept free of
// rendering so it can be tested; Game feeds it the sim's events and React
// draws the timer, the goals and the results.

/** Seconds on the clock at the start of a run. */
export const RUN_TIME = 120;

/** A map's goal list, from its map file (`goals`). */
export interface MapGoals {
  /** High / Pro / Sick score, in that order. */
  scores: [number, number, number];
  /** Gap names (from the map's `gaps`) to land in a run. */
  gaps: string[];
  /** The S-K-A-T-E letters, in order: collecting all five is one goal. */
  letters?: CollectableSpot[];
  /** The C-O-M-B-O letters, in order: all five in one landed combo is one goal. */
  combo?: CollectableSpot[];
  /** The Secret Tape, somewhere hard to reach. */
  tape?: CollectableSpot;
  /**
   * Tasks (#66): the map file's `challenges` entries with a `kind` (a named
   * trick, a big combo...), filled in by `mapFromFile`, not written in the
   * goals block. Free-skate challenges only: a 2-minute run leaves them out.
   */
  tasks?: TaskGoal[];
}

/** What a `Run` covers: all the map's goals for two minutes, or one goal on its own clock (a free-skate challenge). */
export interface RunOptions {
  /** Seconds on the clock (default `RUN_TIME`). */
  time?: number;
  /** Only this goal id: the run tracks nothing else. */
  only?: string;
  /** Track the map's tasks too (free skate's Challenges list); a run with `only` tracks its task anyway. */
  tasks?: boolean;
}

/** Goals for a map file that doesn't list any. */
export const DEFAULT_GOALS: MapGoals = { scores: [10_000, 30_000, 75_000], gaps: [] };

export const SCORE_GOAL_NAMES = ["High Score", "Pro Score", "Sick Score"] as const;

/** A map's goal ids, in goal-list order (the order of `Run.goals`). */
export function goalIds(goals: MapGoals): string[] {
  return [
    ...goals.scores.map((_, i) => `score-${i}`),
    ...(goals.letters?.length ? ["skate"] : []),
    ...(goals.combo?.length ? ["combo"] : []),
    ...goals.gaps.map((name) => `gap-${name}`),
    ...(goals.tape ? ["tape"] : []),
    ...(goals.tasks ?? []).map((t) => t.id),
  ];
}

export interface Goal {
  id: string;
  /** What the goals screen says: "High Score: 15,000", "Land the Funbox Transfer gap". */
  text: string;
  done: boolean;
  /** How far along a collect goal is, like "S K · · E". */
  progress?: string;
  /** Free skate's Challenges list (#66): its tier on the ladder, the stat points it pays, and what it waits on (null when open). */
  tier?: number;
  statPoints?: number;
  needs?: string | null;
}

/**
 * `running` while the clock is going; `overtime` after the buzzer while the
 * combo in progress finishes (it still counts); `done` once it has.
 */
export type RunPhase = "running" | "overtime" | "done";

/**
 * One goal and how it gets done: `event` sees each sim event, `check` runs
 * after them every step. Either returns true when the goal is done. A goal
 * kind is one of these, made in `Run`'s constructor.
 */
interface Tracker {
  goal: Goal;
  /** `ride` is the combo in progress's longest manual and grind so far. */
  event?(e: SkaterEvent, ride: Ride): boolean;
  check?(score: number): boolean;
}

export class Run {
  timeLeft = RUN_TIME;
  phase: RunPhase = "running";
  score = 0;
  readonly goals: Goal[];
  /** Bumped whenever a goal is completed or something is picked up, so the HUD knows to redraw. */
  version = 0;
  /** The run's pickups (letters and tape), placed in the park; see `shown` for which are still there. */
  readonly pickups: readonly Collectable[];
  /** Every goal, in the order they're checked (see the constructor). */
  private trackers: Tracker[] = [];
  private skateGoal: Goal | null = null;
  /** S-K-A-T-E letters and the tape, once picked up (they stay picked up). */
  private taken = new Set<string>();
  /** C-O-M-B-O letters grabbed in the combo in progress; they come back when it ends. */
  private comboHeld = new Set<string>();
  /** The manual or grind going on now, and the combo in progress's longest of each (timed tasks). */
  private rideNow: Ride = { manual: 0, grind: 0 };
  private rideBest: Ride = { manual: 0, grind: 0 };

  /**
   * `pickups` are the map's collectables placed in its level
   * (`placeCollectables`). With `only`, the run tracks that one goal and just
   * the pickups it needs.
   */
  constructor(goals: MapGoals, pickups: readonly Collectable[] = [], opts: RunOptions = {}) {
    const keep = (id: string) => opts.only === undefined || id === opts.only;
    this.timeLeft = opts.time ?? RUN_TIME;
    this.pickups = pickups.filter((c) => keep(c.kind));
    const has = (kind: Collectable["kind"]) => this.pickups.some((c) => c.kind === kind);
    const add = (t: Tracker) => {
      if (keep(t.goal.id)) this.trackers.push(t);
    };
    // Trackers run in this order (so goals done on the same step come out in
    // it); the goal list itself is in `goalIds` order.
    for (const name of goals.gaps) add(gapTracker(name));
    if (has("combo"))
      add({
        goal: { id: "combo", text: "Collect C-O-M-B-O in one combo", done: false },
        event: (e) => e.type === "landed" && this.count("combo", this.comboHeld) === COMBO_LETTERS.length,
      });
    if (has("skate")) {
      this.skateGoal = { id: "skate", text: "Collect S-K-A-T-E", done: false, progress: "" };
      add({ goal: this.skateGoal, check: () => this.count("skate", this.taken) === SKATE_LETTERS.length });
    }
    if (has("tape")) add({ goal: { id: "tape", text: "Find the Secret Tape", done: false }, check: () => this.taken.has("tape") });
    goals.scores.forEach((points, i) =>
      add({
        goal: { id: `score-${i}`, text: `${SCORE_GOAL_NAMES[i]}: ${points.toLocaleString("en-US")}`, done: false },
        check: (score) => score >= points,
      }),
    );
    for (const t of goals.tasks ?? []) {
      if (opts.tasks || opts.only === t.id) add(taskTracker(t));
    }
    this.goals = this.trackers.map((t) => t.goal).sort((a, b) => listRank(a.id) - listRank(b.id));
    this.updateProgress();
  }

  /** Whether a pickup is still in the park to be grabbed. */
  shown(c: Collectable): boolean {
    return !this.taken.has(c.id) && !this.comboHeld.has(c.id);
  }

  /**
   * Picks up whatever the skater, on the board at (x, y, z), is touching.
   * Call it every sim step. C-O-M-B-O letters only come off while a combo
   * (or an air, grind or lip trick) is going: `busy`, as for `step`.
   * Returns what was picked up; the goals they finish complete in `step`.
   */
  touch(x: number, y: number, z: number, busy: boolean): Collectable[] {
    if (this.phase === "done") return [];
    const got: Collectable[] = [];
    for (const c of this.pickups) {
      if (!this.shown(c) || (c.kind === "combo" && !busy) || !touches(c, x, y, z)) continue;
      (c.kind === "combo" ? this.comboHeld : this.taken).add(c.id);
      got.push(c);
    }
    if (got.length) {
      this.updateProgress();
      this.version++;
    }
    return got;
  }

  /** "S K · · E": which letters of a word have been grabbed. */
  letterProgress(kind: "skate" | "combo"): string {
    const held = kind === "skate" ? this.taken : this.comboHeld;
    const word = kind === "skate" ? SKATE_LETTERS : COMBO_LETTERS;
    return word.map((l, i) => (held.has(`${kind}-${i}`) ? l : "·")).join(" ");
  }

  /** How many of a word's letters have been grabbed (C-O-M-B-O's in the combo in progress). */
  lettersGot(kind: "skate" | "combo"): number {
    return this.count(kind, kind === "skate" ? this.taken : this.comboHeld);
  }

  private updateProgress(): void {
    if (this.skateGoal) this.skateGoal.progress = this.letterProgress("skate");
  }

  private count(kind: Collectable["kind"], held: Set<string>): number {
    return this.pickups.filter((c) => c.kind === kind && held.has(c.id)).length;
  }

  get goalsDone(): number {
    return this.goals.filter((g) => g.done).length;
  }

  /**
   * Advances the run by `dt` of sim time. `score` is the skater's score this
   * run; `busy` means a combo (or an air) is still in progress, so the buzzer
   * waits for it. `riding` (what the skater is on this step) times the
   * manual and grind tasks. Returns the goals completed by this step.
   */
  step(dt: number, score: number, busy: boolean, events: readonly SkaterEvent[], riding?: Riding): Goal[] {
    if (this.phase === "done") return [];
    const completed: Goal[] = [];
    const complete = (g: Goal) => {
      if (g.done) return;
      g.done = true;
      completed.push(g);
    };
    for (const k of ["manual", "grind"] as const) {
      this.rideNow[k] = riding?.[k] ? this.rideNow[k] + dt : 0;
      this.rideBest[k] = Math.max(this.rideBest[k], this.rideNow[k]);
    }
    for (const e of events) {
      for (const t of this.trackers) if (t.event?.(e, this.rideBest)) complete(t.goal);
      if (e.type === "landed" || e.type === "bail") {
        this.comboHeld.clear();
        this.rideBest = { ...this.rideNow };
      }
    }
    // Letters grabbed in a combo that has ended (banked or never started) go back.
    if (!busy && this.comboHeld.size) {
      this.comboHeld.clear();
      this.version++;
    }
    this.score = score;
    for (const t of this.trackers) if (t.check?.(score)) complete(t.goal);

    if (this.phase === "running") {
      this.timeLeft = Math.max(0, this.timeLeft - dt);
      if (this.timeLeft === 0) this.phase = "overtime";
    }
    if (this.phase === "overtime" && !busy) this.phase = "done";
    if (completed.length) this.version++;
    return completed;
  }
}

/** Where a goal goes in the goal list (as in `goalIds`): scores, S-K-A-T-E, C-O-M-B-O, gaps, the tape, then tasks. */
function listRank(id: string): number {
  if (id.startsWith("score-")) return 0;
  if (id.startsWith("gap-")) return 3;
  return ["skate", "combo"].indexOf(id) + 1 || (id === "tape" ? 4 : 5);
}

/** A gap goal: the gap goes into the combo, and counts once that combo lands. */
function gapTracker(name: string): Tracker {
  let pending = false;
  return {
    goal: { id: `gap-${name}`, text: `Land the ${name} gap`, done: false },
    event(e) {
      if (e.type === "gap" && e.name === name) pending = true;
      else if (e.type === "bail") pending = false;
      else if (e.type === "landed") {
        const done = pending;
        pending = false;
        return done;
      }
      return false;
    },
  };
}

type Ride = Required<Pick<LandedCombo, "manual" | "grind">>;

/**
 * A task (#66): done by the first landed combo its kind's checker accepts,
 * or for `targets` (#137) and `graffiti` (#151), once enough different
 * targets burst, or tags are washed off, on its clock.
 */
function taskTracker(t: TaskGoal): Tracker {
  const kind = TASK_KINDS[t.kind];
  const hit = new Set<number>();
  return {
    goal: { id: t.id, text: kind.text(t), done: false },
    event(e, ride) {
      if ((e.type === "target" || e.type === "clean") && e.type === kind.counts) return kind.hits!(t, hit.add(e.index).size);
      return e.type === "landed" && kind.done(t, { entries: comboEntries(e.combo), points: e.points, ...ride });
    },
  };
}

/** What the skater is riding, for `Run.step`'s timed tasks. */
export function riding(sk: Pick<Skater, "mode" | "manual">): Riding {
  return { manual: sk.mode === "ground" && sk.manual !== null, grind: sk.mode === "grind" };
}

/** A combo (or an air, grind or lip trick) is still going, so the buzzer waits for it. */
export function runBusy(sk: Pick<Skater, "combo" | "mode">): boolean {
  return sk.combo.active || sk.mode === "air" || sk.mode === "grind" || sk.mode === "lip";
}
